import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useFormValidation from 'hooks/useFormValidation';

const identity = (value) => value;
const identityField = (_, value) => value;

/**
 * Manages a single auto-saving form.
 *
 * `persist(values)` must resolve only after the values have been saved and
 * reject or throw when saving fails. Requests are serialized, and only a save
 * for the current draft may update the persisted baseline. On unmount, queued
 * saves are skipped and active saves cannot update local state or show toasts.
 *
 * When `fields` is provided, `updateField`, `saveField`, and `updateAndSave`
 * accept only values declared in that field map. This makes invalid field usage
 * fail fast while still allowing optional fields absent from `initialValues`.
 */
const useAutoSaveForm = ({
    fields,
    getErrorMessage,
    initialValues,
    normalize = identity,
    normalizeField = identityField,
    onError,
    onSuccess,
    persist,
}) => {
    const [form, setForm] = useState(initialValues);
    const [isDirty, setIsDirty] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const draftRef = useRef(initialValues);
    const savedFormRef = useRef(initialValues);
    const queue = useRef(Promise.resolve());
    const draftRevision = useRef(0);
    const isMounted = useRef(true);
    const { errors, validate, validateAll } = useFormValidation(form, getErrorMessage);
    const knownFieldNames = useMemo(() => (fields ? Object.values(fields) : null), [fields]);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const commitDraft = useCallback((next) => {
        draftRef.current = next;
        setForm(next);
        setIsDirty(!isEqual(next, savedFormRef.current));
    }, []);

    const assertKnownField = useCallback(
        (key) => {
            const isKnownField = knownFieldNames
                ? knownFieldNames.includes(key)
                : Object.prototype.hasOwnProperty.call(draftRef.current, key);
            if (!isKnownField) throw new Error(`Unknown auto-save form field: ${String(key)}`);
        },
        [knownFieldNames]
    );

    const updateField = useCallback(
        (key, value) => {
            assertKnownField(key);
            const normalizedValue = normalizeField(key, value);
            if (isEqual(draftRef.current[key], normalizedValue)) return;

            const next = { ...draftRef.current, [key]: normalizedValue };
            draftRevision.current += 1;
            commitDraft(next);
            if (hasSubmitted || errors[key]) validate(key, value);
        },
        [assertKnownField, commitDraft, errors, hasSubmitted, normalizeField, validate]
    );

    const save = useCallback(async () => {
        const values = draftRef.current;
        if (!hasSubmitted) setHasSubmitted(true);
        if (!validateAll(values)) return false;

        const normalizedValues = normalize(values);
        if (isEqual(normalizedValues, savedFormRef.current)) {
            commitDraft(normalizedValues);
            return true;
        }

        const savedDraftRevision = draftRevision.current;
        const run = async () => {
            if (!isMounted.current) return { cancelled: true };
            try {
                await persist(normalizedValues);
                return { success: true };
            } catch (error) {
                return { error, success: false };
            }
        };

        const pending = queue.current.then(run, run);
        queue.current = pending;
        const result = await pending;
        if (!isMounted.current || result.cancelled) return false;

        const isCurrent = savedDraftRevision === draftRevision.current;

        if (result.success) {
            if (isCurrent) {
                savedFormRef.current = normalizedValues;
                commitDraft(normalizedValues);
                onSuccess?.(normalizedValues);
            }
            return true;
        }

        if (isCurrent) onError?.(result.error);
        return false;
    }, [commitDraft, hasSubmitted, normalize, onError, onSuccess, persist, validateAll]);

    const saveField = useCallback(
        (key) => {
            assertKnownField(key);
            return isEqual(draftRef.current[key], savedFormRef.current[key]) ? Promise.resolve(true) : save();
        },
        [assertKnownField, save]
    );

    const updateAndSave = useCallback(
        (key, value) => {
            updateField(key, value);
            return save();
        },
        [save, updateField]
    );

    const reset = useCallback(
        (next) => {
            draftRevision.current += 1;
            savedFormRef.current = next;
            commitDraft(next);
            setHasSubmitted(false);
        },
        [commitDraft]
    );

    return { errors, form, isDirty, reset, save, saveField, updateAndSave, updateField };
};

export default useAutoSaveForm;
